import React, { useState, useEffect, useCallback, useRef } from 'react';
import { ResponsiveGridLayout as RGL } from 'react-grid-layout';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';

import { 
  LayoutGrid, 
  Save, 
  RotateCcw, 
  Settings,
  RefreshCw,
  Edit,
  Check
} from 'lucide-react';

import axiosInstance from '../../utils/api';
import DashboardCard from '../AnalyticsDashboard/components/DashboardCard.jsx';
import CardSkeleton from '../AnalyticsDashboard/components/CardSkeleton.jsx';
import DateRangePicker from '../AnalyticsDashboard/components/DateRangePicker.jsx';
import AutomationSelect from '../AnalyticsDashboard/components/AutomationSelect.jsx';


// Cache for dashboard settings
let settingsCache = null;

// Format date for display
const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

// Format week for display
const formatWeek = (weekStr) => {
  if (!weekStr) return '';
  const date = new Date(weekStr);
  return `Week of ${date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
};

// Dynamic data transformation based on dataKey
const transformDataForCard = (dataKey, chartData) => {
  if (!chartData || !chartData[dataKey]) return [];
  
  const rawData = chartData[dataKey];
  
  // Transform data dynamically based on field types
  return rawData.map(item => {
    const transformed = { ...item };
    
    Object.keys(transformed).forEach(key => {
      const value = transformed[key];
      
      // Format date fields
      if (key === 'day' && typeof value === 'string') {
        transformed[key] = formatDate(value);
      }
      
      // Format week fields
      if (key === 'week' && typeof value === 'string') {
        transformed[key] = formatWeek(value);
      }
      
      // Parse numeric string values (like amounts)
      if (typeof value === 'string' && !isNaN(parseFloat(value)) && key !== 'day' && key !== 'week' && key !== 'vendor') {
        transformed[key] = parseFloat(value) || 0;
      }
    });
    
    return transformed;
  }).filter(item => {
    // Filter out items with null/empty vendor for vendor-based charts
    if ('vendor' in item) {
      return item.vendor && item.vendor.trim() !== '';
    }
    return true;
  });
};

// Generate grid layout from dashboard items
const generateLayoutFromItems = (items) => {
  return items.map(item => ({
    i: item.id,
    x: item.layout?.x ?? 0,
    y: item.layout?.y ?? 0,
    w: item.layout?.w ?? 6,
    h: item.layout?.h ?? 4,
    minW: item.layout?.minW ?? 3,
    minH: item.layout?.minH ?? 3,
  }));
};

// Convert API item format to DashboardCard expected format
const convertItemFormat = (item) => ({
  id: item.id,
  name: item.name,
  dataKey: item.dataKey,
  configuration: {
    chart_type: item.chart_type,
    mappings: JSON.stringify(item.mappings),
    options: JSON.stringify(item.options || {}),
  },
});

// Helper to get default date range (last 30 days)
const getDefaultDateRange = () => {
  const end = new Date();
  const start = new Date();
  start.setDate(start.getDate() - 30);
  return { startDate: start, endDate: end };
};

// Format date for API (YYYY-MM-DD)
const formatDateForApi = (date) => {
  if (!date) return '';
  // Handle both Date objects and date strings
  const dateObj = date instanceof Date ? date : new Date(date);
  if (isNaN(dateObj.getTime())) return '';
  const year = dateObj.getFullYear();
  const month = String(dateObj.getMonth() + 1).padStart(2, '0');
  const day = String(dateObj.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const Dashboard = () => {
  // Dashboard configuration state
  const [dashboard, setDashboard] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  
  // Date range filter state
  const [dateRange, setDateRange] = useState(getDefaultDateRange());

  // Automation filter state
  const [selectedAutomationId, setSelectedAutomationId] = useState(null);
  const [availableAutomations, setAvailableAutomations] = useState([]);
  const skipAutomationRefetchRef = useRef(false);
  
  // Data state for each card
  const [cardData, setCardData] = useState({});
  const [cardLoading, setCardLoading] = useState({});
  
  // Dashboard settings from API
  const [dashboardSettings, setDashboardSettings] = useState({});
  const [dashboardSettingsArray, setDashboardSettingsArray] = useState([]); // Store full array for bulk update
  
  // Comparison data (for future use)
  const [comparisonData] = useState({});
  
  // Grid layout state
  const [layouts, setLayouts] = useState({ lg: [] });
  const [defaultLayout, setDefaultLayout] = useState([]);
  const [isLayoutLocked, setIsLayoutLocked] = useState(true);
  
  // Map item IDs to data keys for layout updates
  const [itemIdToDataKey, setItemIdToDataKey] = useState({});
  
  // Container width for responsive grid
  const containerRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(1200);

  // Track container width
  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        const newWidth = containerRef.current.offsetWidth;
        setContainerWidth(newWidth);
      }
    };
    
    // Initial width calculation with a small delay to ensure DOM is ready
    const timeoutId = setTimeout(() => {
      updateWidth();
    }, 0);
    
    // Also use ResizeObserver for more accurate tracking
    let resizeObserver;
    if (containerRef.current && window.ResizeObserver) {
      resizeObserver = new ResizeObserver(() => {
        updateWidth();
      });
      resizeObserver.observe(containerRef.current);
    }
    
    window.addEventListener('resize', updateWidth);
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('resize', updateWidth);
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    };
  }, [dashboard]); // Re-run when dashboard loads

  // Fetch dashboard settings from API
  const fetchDashboardSettings = useCallback(async () => {
    try {
      // Use cache if available
      if (settingsCache) {
        // Return cache in the correct format
        if (settingsCache.map && settingsCache.array) {
          return settingsCache;
        }
        // Legacy cache format - convert to new format
        return { map: settingsCache, array: Object.values(settingsCache) };
      }
      
      const response = await axiosInstance.get('/api/v1/dashboard_settings', {
        params: { status: 'active' }
      });
      
      // Create a map of data_key to settings
      const settingsMap = {};
      const settingsArray = [];
      if (response.data?.data) {
        response.data.data.forEach(setting => {
          settingsMap[setting.data_key] = setting;
          settingsArray.push(setting);
        });
      }
      
      // Store full result object in cache
      const result = { map: settingsMap, array: settingsArray };
      settingsCache = result;
      return result;
    } catch (error) {
      console.error('Failed to fetch dashboard settings:', error);
      // Don't show error toast, settings are optional
      return { map: {}, array: [] };
    }
  }, []);

  // Fetch dashboard data from API
  const fetchDashboardDataFromApi = useCallback(async (startDate, endDate, automationId) => {
    try {
      const params = new URLSearchParams();
      if (startDate) {
        params.append('start_date', formatDateForApi(startDate));
      }
      if (endDate) {
        params.append('end_date', formatDateForApi(endDate));
      }
      if (automationId != null && automationId !== '') {
        params.append('automation_id', String(automationId));
      }
      
      const queryString = params.toString();
      const url = `/api/v1/dashboard_data${queryString ? `?${queryString}` : ''}`;
      const response = await axiosInstance.get(url);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
      toast.error('Failed to fetch dashboard data');
      return null;
    }
  }, []);

  // Combined method to fetch both dashboard data and settings simultaneously
  const fetchDashboardDataAndSettings = useCallback(async (startDate, endDate, automationId) => {
    try {
      // Clear cache to ensure settings API is always called
      settingsCache = null;
      
      // Always fetch both APIs together using Promise.all
      const [apiResponse, settingsResult] = await Promise.all([
        fetchDashboardDataFromApi(startDate, endDate, automationId),
        fetchDashboardSettings()
      ]);
      
      return {
        apiResponse,
        settingsResult
      };
    } catch (error) {
      console.error('Failed to fetch dashboard data and settings:', error);
      toast.error('Failed to fetch dashboard data');
      return {
        apiResponse: null,
        settingsResult: { map: {}, array: [] }
      };
    }
  }, [fetchDashboardDataFromApi, fetchDashboardSettings]);

  // Load dashboard configuration and data
  useEffect(() => {
    if (skipAutomationRefetchRef.current && selectedAutomationId != null) {
      skipAutomationRefetchRef.current = false;
      return;
    }

    const loadDashboard = async () => {
      setIsLoading(true);
      try {
        // Fetch both dashboard data and settings simultaneously
        const { apiResponse, settingsResult } = await fetchDashboardDataAndSettings(
          dateRange.startDate,
          dateRange.endDate,
          selectedAutomationId
        );
        
        const settings = settingsResult.map || {};
        const settingsArray = settingsResult.array || [];
        
        // Store settings in state
        setDashboardSettings(settings);
        setDashboardSettingsArray(settingsArray);

        if (apiResponse?.available_automations) {
          setAvailableAutomations(apiResponse.available_automations);
        }
        if (apiResponse?.automation_id != null && selectedAutomationId == null) {
          skipAutomationRefetchRef.current = true;
          setSelectedAutomationId(apiResponse.automation_id);
        }
        
        // Create mapping of item.id to dataKey
        const idToDataKeyMap = {};
        if (apiResponse && apiResponse.items) {
          apiResponse.items.forEach(item => {
            idToDataKeyMap[item.id] = item.dataKey;
          });
          setItemIdToDataKey(idToDataKeyMap);
        }
        
        if (apiResponse && apiResponse.items) {
          // Convert items to expected format, applying settings if available
          const convertedItems = apiResponse.items.map(item => {
            const setting = settings[item.dataKey];
            
            if (setting) {
              // Use settings from API for this chart
              return {
                id: item.id,
                name: setting.name || item.name,
                dataKey: item.dataKey,
                configuration: {
                  chart_type: setting.chart_type?.toLowerCase() || item.chart_type,
                  mappings: JSON.stringify(setting.mappings || item.mappings),
                  options: JSON.stringify(setting.options || item.options || {}),
                },
                hasSettings: true,
              };
            } else {
              // No settings for this chart - return basic format
              return {
                id: item.id,
                name: item.name,
                dataKey: item.dataKey,
                configuration: {
                  chart_type: item.chart_type,
                  mappings: JSON.stringify(item.mappings || {}),
                  options: JSON.stringify(item.options || {}),
                },
                hasSettings: false,
              };
            }
          });
          
          setDashboard({
            name: 'Dashboard',
            items: convertedItems,
          });
          
          // Generate layout - prefer settings layout, then item layout
          const itemLayout = apiResponse.items.map(item => {
            const setting = settings[item.dataKey];
            const layout = setting?.layout || item.layout;
            
            return {
              i: item.id,
              x: layout?.x ?? 0,
              y: layout?.y ?? 0,
              w: layout?.w ?? 6,
              h: layout?.h ?? 4,
              minW: layout?.minW ?? 3,
              minH: layout?.minH ?? 3,
            };
          });
          
          setDefaultLayout(itemLayout);
          
          // Always use layout from API settings
          setLayouts({ lg: itemLayout });

          // Transform data for each card
          const newCardData = {};
          apiResponse.items.forEach((item) => {
            newCardData[item.id] = transformDataForCard(item.dataKey, apiResponse.chart_data);
          });
          setCardData(newCardData);
        }
      } catch (error) {
        console.error('Failed to load dashboard:', error);
        toast.error('Failed to load dashboard configuration');
      } finally {
        setIsLoading(false);
      }
    };

    loadDashboard();
  }, [fetchDashboardDataAndSettings, dateRange, selectedAutomationId]);

  // Handle layout change - completely ignore if locked
  const handleLayoutChange = useCallback((currentLayout) => {
    // Strict check - only allow changes when explicitly unlocked
    if (isLayoutLocked) {
      // Force revert to current layout if somehow changed while locked
      return;
    }
    setLayouts({ lg: currentLayout });
  }, [isLayoutLocked, layouts.lg]);

  // Aggressively prevent dragging when locked - block all drag events
  useEffect(() => {
    if (!containerRef.current) return;
    
    let cleanupFunctions = [];
    
    // Use setTimeout to ensure DOM is ready
    const timeoutId = setTimeout(() => {
      const gridContainer = containerRef.current?.querySelector('.layout');
      if (!gridContainer) return;
      
      const isInteractiveTarget = (target) =>
        target?.closest?.(
          'input, textarea, select, button, a, label, .no-drag, [contenteditable="true"]'
        );

      const preventDrag = (e) => {
        if (isLayoutLocked && !isInteractiveTarget(e.target)) {
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
          return false;
        }
      };
      
      const preventDragStart = (e) => {
        if (isLayoutLocked && !isInteractiveTarget(e.target)) {
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
          return false;
        }
      };
      
      if (isLayoutLocked) {
        // Add multiple event listeners to block all drag interactions
        gridContainer.addEventListener('mousedown', preventDragStart, true);
        gridContainer.addEventListener('touchstart', preventDragStart, true);
        gridContainer.addEventListener('dragstart', preventDrag, true);
        gridContainer.addEventListener('selectstart', preventDrag, true);
        
        cleanupFunctions.push(() => {
          gridContainer.removeEventListener('mousedown', preventDragStart, true);
          gridContainer.removeEventListener('touchstart', preventDragStart, true);
          gridContainer.removeEventListener('dragstart', preventDrag, true);
          gridContainer.removeEventListener('selectstart', preventDrag, true);
        });
        
        // Block all grid items
        const gridItems = gridContainer.querySelectorAll('.react-grid-item:not(.react-grid-placeholder)');
        gridItems.forEach(item => {
          item.addEventListener('mousedown', preventDragStart, true);
          item.addEventListener('touchstart', preventDragStart, true);
          item.style.pointerEvents = 'none';
          item.style.userSelect = 'none';
          item.style.webkitUserDrag = 'none';
          item.style.cursor = 'default';
          
          cleanupFunctions.push(() => {
            item.removeEventListener('mousedown', preventDragStart, true);
            item.removeEventListener('touchstart', preventDragStart, true);
            item.style.pointerEvents = '';
            item.style.userSelect = '';
            item.style.webkitUserDrag = '';
            item.style.cursor = '';
          });
        });
        
        // Re-enable interactive elements
        const interactiveElements = gridContainer.querySelectorAll('.react-grid-item button, .react-grid-item a, .react-grid-item input, .react-grid-item select');
        interactiveElements.forEach(el => {
          el.style.pointerEvents = 'auto';
          el.style.cursor = 'pointer';
        });
      } else {
        // Re-enable when unlocked
        const gridItems = gridContainer.querySelectorAll('.react-grid-item:not(.react-grid-placeholder)');
        gridItems.forEach(item => {
          item.style.pointerEvents = '';
          item.style.userSelect = '';
          item.style.webkitUserDrag = '';
          item.style.cursor = '';
        });
      }
    }, 100);
    
    return () => {
      clearTimeout(timeoutId);
      cleanupFunctions.forEach(cleanup => cleanup());
    };
  }, [isLayoutLocked, layouts.lg.length]);

  // Safety: Prevent layout changes when locked
  useEffect(() => {
    if (isLayoutLocked && defaultLayout.length > 0) {
      // Small delay to allow state updates to complete after locking
      const timeoutId = setTimeout(() => {
        // If locked and layout somehow changed, revert to default
        const currentLayoutStr = JSON.stringify(layouts.lg);
        const defaultLayoutStr = JSON.stringify(defaultLayout);
        if (currentLayoutStr !== defaultLayoutStr) {
          console.warn('Layout changed while locked, reverting...');
          setLayouts({ lg: defaultLayout });
        }
      }, 100);
      
      return () => clearTimeout(timeoutId);
    }
  }, [isLayoutLocked, layouts.lg, defaultLayout]);

  // Force layout recalculation when entering edit mode
  useEffect(() => {
    if (!isLayoutLocked && containerRef.current && layouts.lg.length > 0) {
      // Recalculate container width when entering edit mode
      const updateWidth = () => {
        if (containerRef.current) {
          const newWidth = containerRef.current.offsetWidth;
          if (newWidth !== containerWidth) {
            setContainerWidth(newWidth);
          }
        }
      };
      
      // Small delay to ensure DOM is ready
      const timeoutId = setTimeout(updateWidth, 100);
      return () => clearTimeout(timeoutId);
    }
  }, [isLayoutLocked, layouts.lg.length]);

  // Save layout to API using bulk update
  const saveLayout = useCallback(async () => {
    if (!dashboard || !dashboardSettingsArray.length) {
      toast.error('No settings available to save');
      return false;
    }

    try {
      // Create a map of data_key to current layout from grid
      const layoutMap = {};
      layouts.lg.forEach(layoutItem => {
        const dataKey = itemIdToDataKey[layoutItem.i];
        if (dataKey) {
          layoutMap[dataKey] = {
            x: layoutItem.x,
            y: layoutItem.y,
            w: layoutItem.w,
            h: layoutItem.h,
            minW: layoutItem.minW,
            minH: layoutItem.minH,
          };
        }
      });

      // Update settings array with new layouts (only for settings that exist in DB)
      const updatedSettings = dashboardSettingsArray
        .filter(setting => setting.id) // Only include settings with IDs (existing in DB)
        .map(setting => {
          const newLayout = layoutMap[setting.data_key];
          if (newLayout) {
            return {
              ...setting,
              layout: {
                ...setting.layout,
                ...newLayout,
              },
            };
          }
          return setting;
        });

      if (updatedSettings.length === 0) {
        toast.warning('No configured charts found. Please configure charts first.');
        return false;
      }

      // Call bulk update API
      const response = await axiosInstance.post('/api/v1/dashboard_settings/bulk_update', {
        dashboard_settings: updatedSettings,
      });

      // Verify response
      if (!response || !response.data) {
        throw new Error('Invalid response from server');
      }

      // Update local state
      setDashboardSettingsArray(updatedSettings);
      
      // Update settings map
      const updatedSettingsMap = {};
      updatedSettings.forEach(setting => {
        updatedSettingsMap[setting.data_key] = setting;
      });
      setDashboardSettings(updatedSettingsMap);
      
      // IMPORTANT: Update defaultLayout to match the saved layout
      // This prevents the safety useEffect from reverting the saved layout
      setDefaultLayout(layouts.lg);
      
      // Clear cache to force refresh on next load
      settingsCache = null;
      
      toast.success('Layout saved successfully!');
      return true;
    } catch (error) {
      console.error('Failed to save layout:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to save layout. Please try again.';
      toast.error(errorMessage);
      return false;
    }
  }, [layouts, dashboard, dashboardSettingsArray, itemIdToDataKey]);

  // Toggle edit mode and save when done
  const toggleEditMode = useCallback(async () => {
    if (isLayoutLocked) {
      // Currently locked, entering edit mode
      // Check if there are settings to edit
      if (!dashboardSettingsArray.length) {
        toast.warning('No configured charts found. Please configure charts first before editing layout.');
        return;
      }
      setIsLayoutLocked(false);
    } else {
      // Currently editing, done editing - save layout first
      const saveSuccess = await saveLayout();
      // Only lock if save was successful
      if (saveSuccess) {
        // Update defaultLayout to current layout before locking
        // This ensures the safety useEffect doesn't revert the saved layout
        setDefaultLayout(layouts.lg);
        setIsLayoutLocked(true);
      } else {
        // Keep in edit mode if save failed - user can try again
        toast.error('Layout not saved. Please fix the error and try again.');
      }
    }
  }, [isLayoutLocked, saveLayout, dashboardSettingsArray.length]);

  

  // Refresh single card
  const refreshCard = useCallback(async (cardId) => {
    setCardLoading((prev) => ({ ...prev, [cardId]: true }));
    
    try {
      // Fetch both dashboard data and settings simultaneously
      const { apiResponse, settingsResult } = await fetchDashboardDataAndSettings(
        dateRange.startDate,
        dateRange.endDate,
        selectedAutomationId
      );
      
      // Update settings
      if (settingsResult) {
        const settings = settingsResult.map || {};
        const settingsArray = settingsResult.array || [];
        setDashboardSettings(settings);
        setDashboardSettingsArray(settingsArray);
      }
      
      // Update card data
      if (apiResponse && apiResponse.chart_data && apiResponse.items) {
        const item = apiResponse.items.find((i) => i.id === cardId);
        if (item) {
          const transformedData = transformDataForCard(item.dataKey, apiResponse.chart_data);
          setCardData((prev) => ({ ...prev, [cardId]: transformedData }));
        }
      }
    } catch (error) {
      toast.error('Failed to refresh data');
    } finally {
      setCardLoading((prev) => ({ ...prev, [cardId]: false }));
    }
  }, [fetchDashboardDataAndSettings, dateRange, selectedAutomationId]);

  // Refresh all cards
  const refreshAll = useCallback(async () => {
    if (!dashboard) return;
    
    const loadingState = {};
    dashboard.items.forEach((item) => {
      loadingState[item.id] = true;
    });
    setCardLoading(loadingState);

    try {
      // Fetch both dashboard data and settings simultaneously
      const { apiResponse, settingsResult } = await fetchDashboardDataAndSettings(
        dateRange.startDate,
        dateRange.endDate,
        selectedAutomationId
      );
      
      // Update settings
      if (settingsResult) {
        const settings = settingsResult.map || {};
        const settingsArray = settingsResult.array || [];
        setDashboardSettings(settings);
        setDashboardSettingsArray(settingsArray);
      }
      
      // Update card data
      if (apiResponse && apiResponse.chart_data && apiResponse.items) {
        const newCardData = {};
        apiResponse.items.forEach((item) => {
          newCardData[item.id] = transformDataForCard(item.dataKey, apiResponse.chart_data);
        });
        setCardData(newCardData);
        
        toast.success('Dashboard refreshed!');
      }
    } catch (error) {
      toast.error('Failed to refresh dashboard');
    } finally {
      const clearedLoading = {};
      dashboard.items.forEach((item) => {
        clearedLoading[item.id] = false;
      });
      setCardLoading(clearedLoading);
    }
  }, [dashboard, fetchDashboardDataAndSettings, dateRange, selectedAutomationId]);

  // Handle settings saved - refresh the dashboard to apply new settings
  const handleSettingsSaved = useCallback(async (savedSetting) => {
    // Clear the settings cache to force re-fetch
    settingsCache = null;
    
    try {
      // Refresh settings from API
      const settingsResult = await fetchDashboardSettings();
      const settings = settingsResult.map || {};
      const settingsArray = settingsResult.array || [];
      
      setDashboardSettings(settings);
      setDashboardSettingsArray(settingsArray);
      
      // Update dashboard items with new settings
      if (dashboard && dashboard.items) {
        const updatedItems = dashboard.items.map(item => {
          const setting = settings[item.dataKey];
          
          if (setting) {
            // Use settings from API for this chart
            return {
              id: item.id,
              name: setting.name || item.name,
              dataKey: item.dataKey,
              configuration: {
                chart_type: setting.chart_type?.toLowerCase() || item.configuration?.chart_type,
                mappings: JSON.stringify(setting.mappings || {}),
                options: JSON.stringify(setting.options || {}),
              },
              hasSettings: true,
            };
          } else {
            // No settings for this chart - return basic format
            // Handle both string and object formats for mappings/options
            const existingMappings = typeof item.configuration?.mappings === 'string' 
              ? item.configuration.mappings 
              : JSON.stringify(item.configuration?.mappings || {});
            const existingOptions = typeof item.configuration?.options === 'string'
              ? item.configuration.options
              : JSON.stringify(item.configuration?.options || {});
            
            return {
              id: item.id,
              name: item.name,
              dataKey: item.dataKey,
              configuration: {
                chart_type: item.configuration?.chart_type,
                mappings: existingMappings,
                options: existingOptions,
              },
              hasSettings: false,
            };
          }
        });
        
        // Update layout if settings have layout info
        const updatedLayout = updatedItems.map(item => {
          const setting = settings[item.dataKey];
          const layout = setting?.layout || {};
          
          return {
            i: item.id,
            x: layout.x ?? 0,
            y: layout.y ?? 0,
            w: layout.w ?? 6,
            h: layout.h ?? 4,
            minW: layout.minW ?? 3,
            minH: layout.minH ?? 3,
          };
        });
        
        setDashboard({
          ...dashboard,
          items: updatedItems,
        });
        
        setLayouts({ lg: updatedLayout });
        setDefaultLayout(updatedLayout);
      }
      
      toast.success('Chart settings saved and applied!');
    } catch (error) {
      console.error('Failed to refresh settings:', error);
      toast.error('Settings saved but failed to refresh. Please reload the page.');
    }
  }, [dashboard, fetchDashboardSettings]);

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-[1600px] mx-auto">
          <div className="animate-pulse">
            <div className="h-8 w-64 bg-gray-200 rounded-lg mb-6"></div>
            <div className="grid grid-cols-12 gap-4">
              {[...Array(6)].map((_, i) => (
                <div
                  key={i}
                  className={`bg-white rounded-xl border border-gray-100 h-[300px] ${
                    i < 2 ? 'col-span-6' : i === 2 ? 'col-span-8' : 'col-span-4'
                  }`}
                >
                  <CardSkeleton />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!dashboard) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <LayoutGrid size={48} className="mx-auto text-gray-400 mb-4" />
          <h2 className="text-xl font-semibold text-gray-700">No Dashboard Found</h2>
          <p className="text-gray-500 mt-2">Please check your configuration.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />

      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-40">
        <div className="max-w-[1600px] mx-auto px-6 py-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <AutomationSelect
                automations={availableAutomations}
                value={selectedAutomationId}
                onChange={setSelectedAutomationId}
                disabled={isLoading}
              />
            </div>

            <div className="flex items-center gap-3">

              {/* Date Range Filter */}
              <DateRangePicker
                dateRange={dateRange}
                onChange={setDateRange}
              />

            

              {/* Layout Controls */}
              <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
                <button
                  onClick={toggleEditMode}
                  disabled={isLayoutLocked && dashboardSettingsArray.length === 0}
                  className={`flex items-center gap-2 px-3 py-2 rounded-md transition-colors ${
                    !isLayoutLocked 
                      ? 'bg-primary text-white shadow-sm' 
                      : dashboardSettingsArray.length === 0
                      ? 'text-gray-400 cursor-not-allowed opacity-50'
                      : 'text-gray-500 hover:text-gray-700 hover:bg-white'
                  }`}
                  title={
                    isLayoutLocked 
                      ? dashboardSettingsArray.length === 0 
                        ? 'No configured charts. Configure charts first.' 
                        : 'Edit Layout'
                      : 'Done Editing'
                  }
                >
                  {!isLayoutLocked ? (
                    <>
                      <Check size={18} />
                      <span className="text-sm font-medium">Done Editing</span>
                    </>
                  ) : (
                    <>
                      <Edit size={18} />
                      <span className="text-sm font-medium">Edit Layout</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Dashboard Grid */}
      <div ref={containerRef} className="max-w-[1600px] mx-auto px-3 pt-3 pb-3 relative">
        {/* Grid Background - Show when in edit mode */}
        {!isLayoutLocked && containerWidth > 24 && (
          <div 
            className="absolute top-3 left-3 right-3 bottom-3 pointer-events-none"
            style={{
              backgroundImage: `
                linear-gradient(to right, rgba(0, 0, 0, 0.08) 1px, transparent 1px),
                linear-gradient(to bottom, rgba(0, 0, 0, 0.08) 1px, transparent 1px)
              `,
              backgroundSize: `${(containerWidth - 24) / 12}px 80px`,
              backgroundPosition: '0 0',
            }}
          />
        )}
        {containerWidth > 24 && layouts.lg.length > 0 && (
          <RGL
            key={`grid-${containerWidth}-${isLayoutLocked}`}
            className={`layout ${isLayoutLocked ? 'layout-locked' : 'layout-edit'}`}
            layouts={layouts}
            breakpoints={{ lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 }}
            cols={{ lg: 12, md: 10, sm: 6, xs: 4, xxs: 2 }}
            rowHeight={80}
            width={containerWidth - 24}
            isDraggable={!isLayoutLocked}
            isResizable={!isLayoutLocked}
            draggableCancel=".no-drag, button, a, input, select"
            onLayoutChange={handleLayoutChange}
            margin={[8, 8]}
            compactType={null}
            preventCollision={false}
            useCSSTransforms={true}
            allowOverlap={false}
          >
            {dashboard.items.map((item) => (
              <div 
                key={item.id} 
                className={`h-full w-full ${isLayoutLocked ? '' : 'cursor-move edit-mode-card'}`}
              >
                <DashboardCard
                  item={item}
                  data={cardData[item.id] || []}
                  comparisonData={comparisonData[item.id]}
                  isLoading={cardLoading[item.id]}
                  onRefresh={() => refreshCard(item.id)}
                  hasSettings={item.hasSettings !== false}
                  chartSettings={dashboardSettings[item.dataKey] || null}
                  onSettingsSaved={handleSettingsSaved}
                />
              </div>
            ))}
          </RGL>
        )}
      </div>

      {/* Custom Styles */}
      <style>{`
        .react-grid-item {
          position: relative;
          z-index: 1;
        }
        
        /* Locked mode - COMPLETELY prevent dragging and resizing */
        .layout-locked .react-grid-item {
          pointer-events: none !important;
          touch-action: none !important;
          -webkit-user-drag: none !important;
          user-select: none !important;
        }
        
        .layout-locked .react-grid-item:not(.react-grid-placeholder) {
          cursor: default !important;
        }
        
        .layout-locked .react-grid-item > .react-resizable-handle {
          display: none !important;
          pointer-events: none !important;
        }
        
        /* Prevent drag handle from working in locked mode */
        .layout-locked .react-grid-item.react-draggable,
        .layout-locked .react-grid-item.react-draggable-dragging {
          cursor: default !important;
          pointer-events: none !important;
        }
        
        /* Re-enable pointer events for interactive elements inside cards */
        .layout-locked .react-grid-item > * {
          pointer-events: auto !important;
        }
        
        /* Ensure buttons and interactive elements still work */
        .layout-locked .react-grid-item button,
        .layout-locked .react-grid-item a,
        .layout-locked .react-grid-item input,
        .layout-locked .react-grid-item select,
        .layout-locked .react-grid-item [role="button"],
        .layout-locked .react-grid-item [onclick] {
          pointer-events: auto !important;
          cursor: pointer !important;
          touch-action: auto !important;
        }
        
        /* Edit mode - allow dragging */
        .layout-edit .react-grid-item:not(.react-grid-placeholder) {
          cursor: move;
        }
        
        .react-grid-item.react-grid-placeholder {
          background: rgba(227, 98, 80, 0.2);
          border-radius: 12px;
          border: 2px dashed rgba(227, 98, 80, 0.5);
        }
        
        .react-grid-item > .react-resizable-handle {
          background: none;
        }
        
        /* Make entire card draggable area */
        .react-grid-item > div {
          width: 100%;
          height: 100%;
        }
        
        /* Ensure interactive elements remain clickable */
        .react-grid-item button,
        .react-grid-item a,
        .react-grid-item input,
        .react-grid-item select,
        .react-grid-item [role="button"] {
          pointer-events: auto;
          cursor: pointer;
        }
        
        /* Edit mode visual feedback - blue border and shadow */
        .react-grid-item .edit-mode-card > div {
          border: 2px solid #3B82F6 !important;
          box-shadow: 0 4px 12px rgba(59, 130, 246, 0.15) !important;
          transition: all 0.2s ease;
        }
        
        .react-grid-item .edit-mode-card > div:hover {
          border-color: #2563EB !important;
          box-shadow: 0 6px 16px rgba(59, 130, 246, 0.25) !important;
        }
        
        /* Improve drag feedback */
        .react-grid-item.react-draggable-dragging {
          opacity: 0.85;
          z-index: 1000;
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
          transform: scale(1.02);
        }
        
        .react-grid-item.react-draggable-dragging .edit-mode-card > div {
          border-color: #2563EB !important;
          box-shadow: 0 10px 30px rgba(59, 130, 246, 0.3) !important;
        }
        
        /* Visual feedback when hovering in edit mode */
        .react-grid-item:not(.react-draggable-dragging):hover {
          transition: transform 0.2s ease;
        }
        
        .react-grid-item > .react-resizable-handle::after {
          content: '';
          position: absolute;
          right: 6px;
          bottom: 6px;
          width: 8px;
          height: 8px;
          border-right: 2px solid rgba(0, 0, 0, 0.2);
          border-bottom: 2px solid rgba(0, 0, 0, 0.2);
        }
        
        .react-grid-item:hover > .react-resizable-handle::after {
          border-color: rgba(227, 98, 80, 0.5);
        }

        .react-datepicker {
          font-family: 'Poppins', sans-serif !important;
          border: none !important;
        }

        .react-datepicker__header {
          background: white !important;
          border-bottom: 1px solid #f3f4f6 !important;
        }

        .react-datepicker__day--selected,
        .react-datepicker__day--in-selecting-range,
        .react-datepicker__day--in-range {
          background-color: #E36250 !important;
        }

        .react-datepicker__day--keyboard-selected {
          background-color: rgba(227, 98, 80, 0.3) !important;
        }

        .react-datepicker__day:hover {
          background-color: rgba(227, 98, 80, 0.1) !important;
        }
        
        /* Ensure dashboard grid container has proper bottom spacing */
        .layout {
          padding: 0 !important;
          margin: 0 !important;
        }

        .react-grid-layout {
          margin: 0 !important;
        }
        
        /* Ensure cards at bottom are fully visible */
        .react-grid-item {
          box-sizing: border-box;
        }
      `}</style>
    </div>
  );
};

export default Dashboard;
